/**
 * buildMap: chains LaneSpec stages into a complete BuiltMap with zones,
 * spawn platform, and runout.  All geometry is built starting at y=0 going
 * DOWN; after all meshes are placed a vertical translation pass lifts the
 * whole map so the runout lands at y ≈ 256 above ground.
 */
import { BoxGeometry, Mesh, MeshStandardMaterial, Vector3 } from 'three';
import type { ZoneDef } from '../../game/zones';
import { zoneContains } from '../../game/zones';
import { buildLane } from '../laneBuilder';
import { generateDecor, mergeDecor } from './decorations';
import type { BuiltMap, MapSpec } from './types';

// Shared lane material — runtime replaces it when loading from .glb
const LANE_MATERIAL = new MeshStandardMaterial({ color: 0x8d9299, roughness: 0.85 });

// Shared decor material — darker, rougher than lane surface
const DECOR_MATERIAL = new MeshStandardMaterial({ color: 0x4a4f55, roughness: 0.9 });

// Default platform dimensions (Hammer units)
const DEFAULT_PLATFORM_W = 256;
const DEFAULT_PLATFORM_D = 256;
const DEFAULT_HEIGHT_ABOVE = 380;

/**
 * For heading h (degrees, 0 = toward +z, CW positive):
 *   forward dir = (sin h, 0, cos h)
 *   right dir   = (cos h, 0, -sin h)
 */
function headingForwardVec(hDeg: number): Vector3 {
  const h = (hDeg * Math.PI) / 180;
  return new Vector3(Math.sin(h), 0, Math.cos(h));
}

function headingRightVec(hDeg: number): Vector3 {
  const h = (hDeg * Math.PI) / 180;
  return new Vector3(Math.cos(h), 0, -Math.sin(h));
}

/**
 * Apex height of a surf prism cross-section: h = (width/2)*tan(angleDeg).
 */
function apexH(width: number, angleDeg: number): number {
  return (width / 2) * Math.tan((angleDeg * Math.PI) / 180);
}

interface StageResult {
  meshes: Mesh[];
  /** Cursor (y still pre-translation) at the start of this stage's lane */
  entryBefore: Vector3;
  /** Cursor after this stage's lane segments */
  exitCursor: Vector3;
  exitHeading: number;
  platformW: number;
  platformD: number;
  platformHeightAbove: number;
  /** Heading in effect at the start of this stage */
  entryHeading: number;
}

export function buildMap(spec: MapSpec): BuiltMap {
  const colliders: Mesh[] = [];
  const details: Mesh[] = [];

  let cursor = new Vector3(0, 0, 0);
  let headingDeg = 0;

  const stageResults: StageResult[] = [];

  for (let i = 0; i < spec.stages.length; i++) {
    const stage = spec.stages[i];
    if (stage === undefined) throw new Error(`buildMap: stage ${i} missing`);
    const entryHeading = headingDeg;
    const entryBefore = cursor.clone();

    const {
      meshes,
      cursor: nextCursor,
      headingDeg: nextHeading,
    } = buildLane(stage.lane, cursor.clone(), headingDeg, LANE_MATERIAL);

    for (let j = 0; j < meshes.length; j++) {
      const m = meshes[j];
      if (m !== undefined) {
        m.name = `lane_s${i}_${j}`;
      }
    }

    const platformW = stage.platform?.width ?? DEFAULT_PLATFORM_W;
    const platformD = stage.platform?.depth ?? DEFAULT_PLATFORM_D;
    const platformHeightAbove = stage.platform?.heightAbove ?? DEFAULT_HEIGHT_ABOVE;

    stageResults.push({
      meshes,
      entryBefore,
      exitCursor: nextCursor.clone(),
      exitHeading: nextHeading,
      platformW,
      platformD,
      platformHeightAbove,
      entryHeading,
    });

    cursor = nextCursor.clone();
    headingDeg = nextHeading;

    // Apply gap: rotate heading THEN advance cursor, then drop
    if (stage.gap !== undefined) {
      if (stage.gap.turnDeg !== undefined) {
        headingDeg += stage.gap.turnDeg;
      }
      const fwd = headingForwardVec(headingDeg);
      cursor.addScaledVector(fwd, stage.gap.distance);
      if (stage.gap.lateral !== undefined) {
        const right = headingRightVec(headingDeg);
        cursor.addScaledVector(right, stage.gap.lateral);
      }
      cursor.y -= stage.gap.drop;
    }
  }

  // After all stages, compute runout position
  const finalCursor = cursor.clone();
  const finalHeading = headingDeg;

  // Runout platform: top surface = finalCursor.y − 64.
  // Box height 64; center = top − 32.
  const runoutFwd = headingForwardVec(finalHeading);
  const runoutW = Math.max(spec.runout.width, spec.runout.length);
  const runoutD = Math.max(spec.runout.width, spec.runout.length);
  const runoutTopY = finalCursor.y - 64;
  const runoutCenterY = runoutTopY - 32;
  const runoutCenterX = finalCursor.x + runoutFwd.x * (spec.runout.length / 2);
  const runoutCenterZ = finalCursor.z + runoutFwd.z * (spec.runout.length / 2);

  const runoutMesh = new Mesh(new BoxGeometry(runoutW, 64, runoutD), LANE_MATERIAL);
  runoutMesh.name = 'platform_runout';
  runoutMesh.position.set(runoutCenterX, runoutCenterY, runoutCenterZ);

  // Find minY across all geometry (raw, before translation)
  let minY = 0;
  for (const sr of stageResults) {
    minY = Math.min(minY, sr.entryBefore.y, sr.exitCursor.y);
  }
  minY = Math.min(minY, finalCursor.y, runoutCenterY - 32);

  // Offset so the lowest point lands at y = 256
  const offset = -minY + 256;

  // Apply offset to all stage meshes
  for (const sr of stageResults) {
    for (const mesh of sr.meshes) {
      mesh.position.y += offset;
      colliders.push(mesh);
    }
    sr.entryBefore.y += offset;
    sr.exitCursor.y += offset;
  }

  // Translate runout
  runoutMesh.position.y += offset;
  finalCursor.y += offset;
  colliders.push(runoutMesh);

  // Generate decor (visual-only, never added to colliders)
  // Ground plane y: the lowest geometry after translation lands at 256.
  const groundY = 256 - 512; // conservative: 256u below the nominal floor
  const groundPlane = { y: groundY };

  for (let i = 0; i < spec.stages.length; i++) {
    const stage = spec.stages[i];
    if (stage === undefined) continue;
    const sr = stageResults[i];
    if (sr === undefined) continue;

    const effectiveDecor = mergeDecor(spec.decor, stage.decor);
    if (effectiveDecor === null) continue;

    const decorMeshes = generateDecor(
      i,
      sr.meshes,
      stage.lane,
      effectiveDecor,
      groundPlane,
      DECOR_MATERIAL,
    );
    for (const dm of decorMeshes) {
      details.push(dm);
    }
  }

  // Build spawn platforms
  // For stage 0 (always) and any stage with an explicit platform spec.
  const spawnPlatforms: Mesh[] = [];

  for (let i = 0; i < spec.stages.length; i++) {
    const stage = spec.stages[i];
    if (stage === undefined) throw new Error(`buildMap: stage ${i} missing`);
    if (i !== 0 && stage.platform === undefined) {
      continue;
    }
    const sr = stageResults[i];
    if (sr === undefined) throw new Error(`buildMap: stageResult ${i} missing`);

    // Right-hand direction at stage entry heading
    const right = headingRightVec(sr.entryHeading);

    // Platform top = lane base at start + apex height + heightAbove
    const ah = apexH(stage.lane.width, stage.lane.angleDeg);
    const platformTopY = sr.entryBefore.y + ah + sr.platformHeightAbove;
    const platformCenterY = platformTopY - 16; // BoxGeometry 32u tall → half = 16

    // Lateral offset: place platform to the RIGHT of the lane so walk-off drops
    // onto the right slope face.  half lane width + half platform depth + 32 gap.
    const laneHalfWidth = stage.lane.width / 2;
    const platformHalfD = sr.platformD / 2;
    const lateralDist = laneHalfWidth + platformHalfD + 32;

    // Forward offset: move platform 300u into the lane so the player spawns
    // above the interior of the prism (not the uncapped front edge).
    const fwd = headingForwardVec(sr.entryHeading);
    const SPAWN_Z_OFFSET = 300;

    const platform = new Mesh(new BoxGeometry(sr.platformW, 32, sr.platformD), LANE_MATERIAL);
    platform.name = `platform_s${i}`;
    platform.position.set(
      sr.entryBefore.x + right.x * lateralDist + fwd.x * SPAWN_Z_OFFSET,
      platformCenterY,
      sr.entryBefore.z + right.z * lateralDist + fwd.z * SPAWN_Z_OFFSET,
    );
    colliders.push(platform);
    spawnPlatforms.push(platform);
  }

  // Stage entry cursors for return value
  const stageEntries: Vector3[] = stageResults.map((sr) => sr.entryBefore.clone());

  // Spawn point: feet on top of stage-0 platform
  const sp0 = spawnPlatforms[0];
  if (sp0 === undefined) throw new Error(`buildMap(${spec.id}): no spawn platform built`);
  const spawnPoint = new Vector3(sp0.position.x, sp0.position.y + 16, sp0.position.z);

  // spawnYaw: at heading 0, lane runs toward +z; camera should face +z → yaw = PI.
  // Derivation: camera forward = (-sin yaw, 0, -cos yaw); want (sin h, 0, cos h)
  //   → yaw = h + PI.  Normalize to [-PI, PI].
  const firstStageResult = stageResults[0];
  if (firstStageResult === undefined) throw new Error(`buildMap(${spec.id}): no stages`);
  const stage0HeadingRad = (firstStageResult.entryHeading * Math.PI) / 180;
  let spawnYaw = stage0HeadingRad + Math.PI;
  if (spawnYaw > Math.PI) spawnYaw -= 2 * Math.PI;

  // Zones
  const zones: ZoneDef[] = [];

  // 'start' zone: centered on spawn platform top + 110u up
  const sr0 = firstStageResult;
  const startZone: ZoneDef = {
    kind: 'start',
    center: new Vector3(sp0.position.x, sp0.position.y + 16 + 110, sp0.position.z),
    size: new Vector3(sr0.platformW + 60, 240, sr0.platformD + 60),
  };
  zones.push(startZone);

  // 'checkpoint' zones for stages 1..N-1
  for (let i = 1; i < spec.stages.length; i++) {
    const stage = spec.stages[i];
    if (stage === undefined) throw new Error(`buildMap: stage ${i} missing`);
    const stageResult = stageResults[i];
    if (stageResult === undefined) throw new Error(`buildMap: stageResult ${i} missing`);
    const entry = stageResult.entryBefore;
    const ah = apexH(stage.lane.width, stage.lane.angleDeg);

    zones.push({
      kind: 'checkpoint',
      center: new Vector3(entry.x, entry.y + ah * 0.6, entry.z),
      size: new Vector3(stage.lane.width * 2.2, ah * 2.5, 360),
    });
  }

  // 'end' zone: over the runout platform
  const endZone: ZoneDef = {
    kind: 'end',
    center: new Vector3(
      runoutMesh.position.x,
      runoutMesh.position.y + 32 + 230, // top surface + 230
      runoutMesh.position.z,
    ),
    size: new Vector3(spec.runout.width + 400, 460, spec.runout.length + 400),
  };
  zones.push(endZone);

  // Assert start zone contains spawn
  if (!zoneContains(startZone, spawnPoint)) {
    throw new Error(
      `buildMap(${spec.id}): start zone does not contain spawn. ` +
        `spawn=(${spawnPoint.x.toFixed(1)},${spawnPoint.y.toFixed(1)},${spawnPoint.z.toFixed(1)}), ` +
        `zone.center=(${startZone.center.x.toFixed(1)},${startZone.center.y.toFixed(1)},${startZone.center.z.toFixed(1)}), ` +
        `zone.size=(${startZone.size.x.toFixed(1)},${startZone.size.y.toFixed(1)},${startZone.size.z.toFixed(1)})`,
    );
  }

  // Assert each checkpoint zone contains its stage entry
  for (let i = 1; i < spec.stages.length; i++) {
    const zone = zones[i];
    if (zone === undefined) throw new Error(`buildMap: zone ${i} missing`);
    const sr = stageResults[i];
    if (sr === undefined) throw new Error(`buildMap: stageResult ${i} missing`);
    const entry = sr.entryBefore;
    if (!zoneContains(zone, entry)) {
      throw new Error(
        `buildMap(${spec.id}): checkpoint zone ${i - 1} does not contain stage ${i} entry. ` +
          `entry=(${entry.x.toFixed(1)},${entry.y.toFixed(1)},${entry.z.toFixed(1)}), ` +
          `zone.center=(${zone.center.x.toFixed(1)},${zone.center.y.toFixed(1)},${zone.center.z.toFixed(1)}), ` +
          `zone.size=(${zone.size.x.toFixed(1)},${zone.size.y.toFixed(1)},${zone.size.z.toFixed(1)})`,
      );
    }
  }

  // killY: lowest collider bottom − 512
  let lowestBottom = Number.POSITIVE_INFINITY;
  for (const mesh of colliders) {
    if (mesh.name.startsWith('platform')) {
      const h = mesh.name === 'platform_runout' ? 64 : 32;
      lowestBottom = Math.min(lowestBottom, mesh.position.y - h / 2);
    } else {
      // Lane mesh: base of prism is at y=0 in local space; position.y is lower bound
      lowestBottom = Math.min(lowestBottom, mesh.position.y);
    }
  }
  const killY = spec.killY ?? lowestBottom - 512;

  return {
    colliders,
    details,
    zones,
    spawn: spawnPoint,
    spawnYaw,
    killY,
    stageEntries,
  };
}
